import { forwardRef, type InputHTMLAttributes } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  optional?: boolean;
  /** Per-field validation message. Renders below the input and turns the border red. */
  error?: string;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, hint, optional, error, id, name, ...rest }, ref) => {
    const field_id = id ?? name;
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={field_id} className="flex items-center gap-1">
          {label}
          {optional && <span className="text-xs font-normal text-muted-foreground">(optional)</span>}
        </Label>
        <Input
          id={field_id}
          name={name}
          ref={ref}
          aria-invalid={error ? true : undefined}
          className={error ? 'border-destructive focus-visible:border-destructive' : undefined}
          {...rest}
        />
        {error ? (
          <p className="text-xs text-destructive" data-testid={`field-error-${name}`}>
            {error}
          </p>
        ) : (
          hint && <p className="text-xs text-muted-foreground">{hint}</p>
        )}
      </div>
    );
  },
);
TextField.displayName = 'TextField';

export function TextAreaField({
  label,
  hint,
  optional,
  error,
  id,
  name,
  rows = 3,
  ...rest
}: {
  label: string;
  hint?: string;
  optional?: boolean;
  error?: string;
  id?: string;
  name?: string;
  rows?: number;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const field_id = id ?? name;
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={field_id} className="flex items-center gap-1">
        {label}
        {optional && <span className="text-xs font-normal text-muted-foreground">(optional)</span>}
      </Label>
      <textarea
        id={field_id}
        name={name}
        rows={rows}
        aria-invalid={error ? true : undefined}
        className={
          'flex w-full rounded-input border-2 bg-card px-4 py-3 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-50 ' +
          (error
            ? 'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/15'
            : 'border-input focus-visible:border-primary focus-visible:ring-primary/15')
        }
        {...rest}
      />
      {error ? (
        <p className="text-xs text-destructive" data-testid={`field-error-${name}`}>
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}
